import type { TaskEntityType } from '../types/task.types';

/** A record a task can be linked to (every TaskEntityType but the two non-links). */
export type TaskLinkType = Exclude<TaskEntityType, 'external' | 'none'>;

export interface TaskLinkTypeModel {
	type: TaskLinkType;
	/** Title Case name shown in the picker, the Linked to filter and the task window. */
	label: string;
	/** The record's page; the link opens `${pagePath}/${id}`. */
	pagePath: string;
	/** Capability a tenant needs before tasks may link to this type; null = always. */
	capability: string | null;
	/**
	 * False while the record's table does not exist yet: the type is not
	 * offered, cannot be linked, and is never picked as a task's link.
	 */
	available: boolean;
	/**
	 * False for a type only the system links (a campaign call-back, made from
	 * the campaign): it shows on tasks and in the Linked to filter, but the
	 * "Link to" picker and the assistant never offer it.
	 */
	userLinkable: boolean;
	/** Position in the "Link to" picker (the array order is the link priority). */
	pickerOrder: number;
	/** Row column holding the linked id in every task query (see crm-api task-links.ts). */
	idColumn: string;
}

/**
 * PD-17601: the one list of task link types. A task can carry several links
 * (an issue task also links the issue's customer), and the record it is
 * "linked to" is the first type in this order it has a link for. Before this
 * list, six task queries each wrote their own priority chain; four of them
 * did not know vendors, so a vendor task showed as unlinked once it was
 * opened or saved. Everything that names a link type reads this list: the
 * task queries, the access check, the "Link to" picker, the Tasks list's
 * Linked to filter and column, and the assistant's task tools.
 */
export const TASK_LINK_TYPES: ReadonlyArray<TaskLinkTypeModel> = [
	{ type: 'issue', label: 'Issue', pagePath: '/issues', capability: null, available: true, userLinkable: true, pickerOrder: 3, idColumn: 'issueId' },
	{ type: 'license', label: 'License', pagePath: '/licenses', capability: null, available: true, userLinkable: true, pickerOrder: 4, idColumn: 'licenseId' },
	{ type: 'opportunity', label: 'Opportunity', pagePath: '/opportunities', capability: null, available: true, userLinkable: true, pickerOrder: 2, idColumn: 'opportunityId' },
	{
		type: 'sourcing_request',
		label: 'Sourcing Request',
		pagePath: '/sourcing-requests',
		capability: null,
		available: true,
		userLinkable: true,
		pickerOrder: 5,
		idColumn: 'sourcingRequestId',
	},
	// A call-back booked for a set time during a campaign; the link opens the
	// person's campaign card. Campaign steps themselves are never tasks
	// (owner, 10/26/26). Turned on with the campaign tables (PD-17613).
	{
		type: 'campaign_member',
		label: 'Call-back',
		pagePath: '/campaigns/members',
		capability: 'campaigns',
		available: true,
		userLinkable: false,
		pickerOrder: 8,
		idColumn: 'campaignMemberId',
	},
	// A show's to-dos and deadlines (PD-17632).
	{
		type: 'trade_show',
		label: 'Trade Show',
		pagePath: '/trade-shows',
		capability: 'trade_shows',
		available: true,
		userLinkable: true,
		pickerOrder: 7,
		idColumn: 'tradeShowId',
	},
	// A membership's renewal steps and a show's "join or renew" to-do (PD-17640).
	{
		type: 'association_membership',
		label: 'Association',
		pagePath: '/associations/membership',
		capability: 'trade_shows',
		available: true,
		userLinkable: false,
		pickerOrder: 9,
		idColumn: 'associationMembershipId',
	},
	{ type: 'vendor', label: 'Vendor', pagePath: '/vendors', capability: null, available: true, userLinkable: true, pickerOrder: 6, idColumn: 'vendorId' },
	{ type: 'client', label: 'Client', pagePath: '/clients', capability: null, available: true, userLinkable: true, pickerOrder: 1, idColumn: 'clientId' },
];

export function findTaskLinkType(type: string | null | undefined): TaskLinkTypeModel | undefined {
	return TASK_LINK_TYPES.find((entry) => entry.type === type);
}

/** The types a person can link a task to today, in picker order. */
export function linkableTaskLinkTypes(hasCapability: (capability: string) => boolean): Array<TaskLinkTypeModel> {
	return TASK_LINK_TYPES.filter(
		(entry) => entry.available && entry.userLinkable && (entry.capability == null || hasCapability(entry.capability)),
	).sort(
		(left, right) => left.pickerOrder - right.pickerOrder,
	);
}

/**
 * The record a task is linked to, from the ids of every link it carries
 * (keyed by type): the first available type, in priority order, with an id.
 */
export function resolveTaskLink(ids: Partial<Record<TaskLinkType, number | null | undefined>>): {
	entityType: TaskEntityType;
	entityId: number | null;
} {
	for (const entry of TASK_LINK_TYPES) {
		const id = ids[entry.type];

		if (entry.available && id != null) {
			return { entityType: entry.type, entityId: Number(id) };
		}
	}

	return { entityType: 'none', entityId: null };
}

/** Same as resolveTaskLink, reading each type's `idColumn` from a task query row. */
export function resolveTaskLinkFromRow(row: Record<string, unknown>): {
	entityType: TaskEntityType;
	entityId: number | null;
} {
	const ids: Partial<Record<TaskLinkType, number | null>> = {};

	for (const entry of TASK_LINK_TYPES) {
		ids[entry.type] = (row[entry.idColumn] as number | null | undefined) ?? null;
	}

	return resolveTaskLink(ids);
}

/** The page a task's linked record opens, or null for no link. */
export function taskLinkUrl(entityType: TaskEntityType, entityId: number | null): string | null {
	const entry = findTaskLinkType(entityType);

	return entry == null || entityId == null ? null : `${entry.pagePath}/${entityId}`;
}
